<!-- Intake: sticky top progress for standalone; brief per-question flash only in iframes. -->
<div class="survey-intake survey-container">
  <header class="survey-intake__header" aria-label="Survey introduction">
    <h1 class="survey-intake__title">{{ survey?.surveyTypeName ?? 'Survey' }}</h1>
    <p class="survey-intake__intro">Please answer the questions below.</p>
  </header>

  @if (progressTotalCount > 0 && !isPreview) {
    <div class="survey-intake__progress" role="region" aria-label="Survey progress">
      <p class="survey-intake__progress-text">
        <span class="survey-intake__progress-count">{{ progressAnsweredCount }} of {{ progressTotalCount }}</span> questions answered
        @if (progressFlavorText) {
          <span class="survey-intake__progress-flavor"> — {{ progressFlavorText }}</span>
        }
      </p>
      <div
        class="survey-intake__progress-bar"
        role="progressbar"
        [attr.aria-valuenow]="progressAnsweredCount"
        aria-valuemin="0"
        [attr.aria-valuemax]="progressTotalCount"
        [attr.aria-valuetext]="progressAnsweredCount + ' of ' + progressTotalCount + ' questions answered'"
      >
        <div class="survey-intake__progress-fill" [style.width.%]="progressPercent"></div>
      </div>
    </div>
  }

  @if (progressFlashQuestionId != null && progressTotalCount > 0 && !isPreview) {
    <p class="sr-only" role="status" aria-live="polite">
      {{ progressAnsweredCount }} of {{ progressTotalCount }} questions answered
      @if (progressFlavorText) {
        {{ progressFlavorText }}
      }
    </p>
  }

  <form
    class="survey-intake__form survey-form"
    role="form"
    aria-labelledby="survey-questions-heading"
    id="survey-intake-form"
    (ngSubmit)="submit()"
  >
    <h2 id="survey-questions-heading" class="sr-only">Survey questions</h2>

    @for (question of getQuestionOrdered(); track question.id) {
      <section class="survey-intake__question" [attr.aria-labelledby]="'q-label-' + question.id">
        @if (isProgressFlashVisible(question.id)) {
          <div class="survey-intake__question-progress" role="status">
            <span class="survey-intake__question-counter">
              {{ progressAnsweredCount }} of {{ progressTotalCount }} answered
              @if (progressFlavorText) {
                <span class="survey-intake__progress-flavor"> — {{ progressFlavorText }}</span>
              }
            </span>
            <div class="survey-intake__progress-bar survey-intake__progress-bar--compact">
              <div class="survey-intake__progress-fill" [style.width.%]="progressPercent"></div>
            </div>
          </div>
        }
        <h3 [id]="'q-label-' + question.id" class="survey-intake__question-title">
          {{ question.questionText }}
        </h3>
        @if (question.questionType === 'Radio') {
          <fieldset class="survey-form__fieldset" [attr.aria-describedby]="'q-desc-' + question.id">
            <legend class="sr-only">{{ question.questionText }}</legend>
            <ul class="survey-form__radio-list" role="radiogroup">
              @for (answer of getAnswersOrdered(question); track answer.id) {
                <li>
                  <label class="survey-form__radio-label">
                    <input
                      type="radio"
                      [name]="'q-' + question.id"
                      [value]="answer.id"
                      class="survey-form__radio"
                      [attr.aria-describedby]="'q-desc-' + question.id"
                      [checked]="getRadioValue(question.id) === answer.id"
                      (change)="setRadio(question.id, answer.id)"
                    />
                    <span>{{ answer.answerText }}{{ isPreview && answer.score != null ? ' (' + answer.score + ' pts)' : '' }}</span>
                  </label>
                </li>
              }
            </ul>
            <p [id]="'q-desc-' + question.id" class="sr-only">Select one option.</p>
          </fieldset>
        }
        @if (question.questionType === 'FreeFormText') {
          <div class="survey-form__text-block">
            <label class="survey-form__label sr-only" [for]="'q-free-' + question.id">Answer</label>
            <textarea
              class="survey-form__textarea survey-form__input"
              [id]="'q-free-' + question.id"
              [name]="'q-free-' + question.id"
              rows="3"
              [maxlength]="maxChars(question)"
              placeholder="Your answer..."
              [attr.aria-describedby]="'q-free-desc-' + question.id"
              [ngModel]="getFreeFormValue(question.id)"
              (ngModelChange)="setFreeForm(question.id, $event)"
              name="free-{{ question.id }}"
            ></textarea>
            <p [id]="'q-free-desc-' + question.id" class="survey-form__char-count">
              <span>{{ charCount(question.id) }}</span> / {{ maxChars(question) }} characters
            </p>
          </div>
        }
      </section>
    }

    @if (errorMessage || submitError) {
      <p class="survey-form__error" role="alert">{{ errorMessage ?? submitError }}</p>
    }

    <div class="survey-intake__actions">
      <button
        type="submit"
        class="btn btn-primary survey-intake__submit"
        [disabled]="submitInProgress || isPreview"
        [attr.aria-busy]="submitInProgress"
      >
        {{ submitInProgress ? 'Getting your results...' : 'SEE YOUR RESULTS' }}
      </button>
    </div>
  </form>
</div>
